import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../../components/Pagination";

const ScheduleList = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);
  const [activeStatus, setActiveStatus] = useState("");
  const [message, setMessage] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  // Copy dialog states
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [subusers, setSubusers] = useState([]);
  const [loadingSubusers, setLoadingSubusers] = useState(false);
  const [selectedSubuser, setSelectedSubuser] = useState("");
  const [selectedScheduleForCopy, setSelectedScheduleForCopy] = useState(null);
  const [messageType, setMessageType] = useState(""); // Track message type for styling

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  const fetchScheduleData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/CatalogSchedulerOperation/SubUser/Search',
        {
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string"
        },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
        setTotalItems(response.data.data.length);
      } else {
        setData([]);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching schedule data:", error);
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchScheduleData();
  }, [getUrlByOperation, token, userid, dataLimit, count, from, to, activeStatus]);

  // Function to fetch subusers
  const fetchSubusers = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      console.error("Missing token or userId in localStorage");
      return;
    }

    setLoadingSubusers(true);
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.bonrix.in/api/SubUserOperation/SubUser/Search',
        {
          "id": 0,
          "applycreateddate": false,
          "applymodifieddate": false,
          "fromcreateddate": 0,
          "endcreateddate": 0,
          "frommodifieddate": 0,
          "endmodifieddate": 0,
          "active": "",
          "catalogname": "",
          "ispageload": false,
          "pagesize": 10,
          "currentpageno": 0,
          "withoutpagination": false,
          "pagedirection": "first"
        },
        {
          headers: {
            'X-Authorization': token,
            'UserId': userid,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data)) {
        setSubusers(response.data.data);
      } else {
        setSubusers([]);
      }
    } catch (error) {
      console.error("Error fetching subusers:", error);
      setSubusers([]);
    } finally {
      setLoadingSubusers(false);
    }
  };

  // Function to open copy dialog
  const handleOpenCopyDialog = (item) => {
    setSelectedScheduleForCopy(item);
    setSelectedSubuser("");
    setShowCopyDialog(true);
    fetchSubusers();
  };

  // Function to close copy dialog
  const handleCloseCopyDialog = () => {
    setShowCopyDialog(false);
    setSelectedScheduleForCopy(null);
    setSelectedSubuser("");
    setSubusers([]);
  };


  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddPop = () => {
    navigate("/addschedule");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updateschedule/${id}`);
  };

  const handleShowPop = (id, name) => {
    navigate(`/showschedule/${id}/${name}`);
  };

  const handleDelete = async (id) => {
    try {
      await axios.post('https://api.dqr-display-tms.bonrix.in/api/CatalogSchedulerOperation/SubUser/Delete', { id }, {
        headers: {
          'accept': 'text/plain',
          'UserId': userid,
          'X-Authorization': token,
          'Content-Type': 'application/json-patch+json',
        },
      });
      setMessage("Data deleted successfully!");
      fetchScheduleData();
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
    }
  };

  const handleToggle = async (item) => {
    const apiUrl = item.active 
      ? 'https://api.dqr-display-tms.bonrix.in/api/CatalogSchedulerOperation/SubUser/UpdateStatus/Deactive'
      : 'https://api.dqr-display-tms.bonrix.in/api/CatalogSchedulerOperation/SubUser/UpdateStatus/Active';

    try {
      await axios.post(apiUrl, { id: item.id }, {
        headers: {
          'accept': 'text/plain',
          'UserId': userid,
          'X-Authorization': token,
          'Content-Type': 'application/json-patch+json'
        },
      });
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Schedules</h1>
                <button type="button" className="btn btn-success" onClick={handleAddPop}>
                  Add Schedule
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                        <thead className="thead-light">
                          <tr>
                            <th style={{width: '5%'}} className="text-center">Id</th>
                            <th style={{width: '25%'}}>Name</th>
                            <th style={{width: '20%'}}>Remark</th>
                            <th style={{width: '25%'}} className="text-center">Action</th>
                            <th style={{width: '10%'}} className="text-center">Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item, index) => (
                            <tr key={index}>
                              <td className="text-center">{item.id}</td>
                              <td>
                                <strong>{item.name}</strong>
                              </td>
                              <td>{item.remark || "-"}</td>
                              <td className="text-center">
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-info mr-1"
                                    onClick={() => handleShowPop(item.id, item.name)}
                                  >
                                    <i className="fas fa-eye mr-1"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-warning mr-1"
                                    onClick={() => handleUpdatePop(item.id)}
                                  >
                                    <i className="fas fa-edit mr-1"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger mr-1"
                                    onClick={() => handleDelete(item.id)}
                                  >
                                    <i className="fas fa-trash mr-1"></i>
                                  </button>
                                </div>
                              </td>
                              <td className="text-center">
                                <Switch
                                  isOn={item.active}
                                  handleToggle={() => handleToggle(item)}
                                  id={item.id}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <Pagination
                      currentPage={count + 1}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={totalItems}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Message display - show both as toast notification AND component */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "10px", width: "25%", zIndex: "9999" }}>
          <div
            className={`alert ${messageType === "success" ? "alert-success" : "alert-danger"} d-flex align-items-center`}
            role="alert"
            style={{ marginBottom: "10px" }}
          >
            <div>{message}</div>
          </div>
        </div>
      )}
      
      {message === "Data deleted successfully!" && <SuccessMessage message={message} />}
      {message === "Failed to delete data" && <FailedMessage message={message} />}
      
      {/* Copy Dialog */}
      {showCopyDialog && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "block"
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document" style={{ width: "100%", maxWidth: "400px" }}>
            <div className="modal-content" style={{ borderRadius: "15px", border: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #f0f0f0", padding: "1.5rem" }}>
                <h5 className="modal-title" style={{ fontWeight: "700", color: "#333", fontSize: "1.25rem" }}>
                  <i className="fas fa-copy mr-2 text-success"></i>
                  Copy Schedule to Subuser
                </h5>
                <button type="button" className="close" onClick={handleCloseCopyDialog} style={{ outline: "none" }}>
                  <span aria-hidden="true" style={{ fontSize: "1.5rem" }}>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ padding: "1.5rem" }}>
                {message && (
                  <div className="alert alert-info" role="alert">
                    {message}
                  </div>
                )}

                <div className="form-group">
                  <label style={{ fontWeight: "600", color: "#495057", marginBottom: "0.5rem", display: "block" }}>
                    <i className="fas fa-user mr-2 text-muted"></i>Select Subuser
                  </label>
                  <select
                    className="form-control custom-select"
                    value={selectedSubuser}
                    onChange={(e) => setSelectedSubuser(e.target.value)}
                    style={{ borderRadius: "8px", padding: "0.6rem 1rem", height: "auto" }}
                    disabled={loadingSubusers}
                  >
                    <option value="">{loadingSubusers ? "Loading subusers..." : "Select subuser..."}</option>
                    {subusers.map((subuser, index) => (
                      <option key={index} value={subuser.id}>
                        {subuser.username}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #f0f0f0", padding: "1.25rem 1.5rem", justifyContent: "space-between" }}>
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseCopyDialog}
                  style={{ fontWeight: "600", color: "#6c757d", padding: "0.6rem 1.5rem" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ScheduleList;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}
